import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

interface PaymentStatus {
  paymentId: string;
  status: string;
  statusDetail: string;
  amount: number;
  currency: string;
  externalReference: string;
  timestamp: string;
}

interface LocationState {
  paymentStatus?: PaymentStatus;
  message?: string;
}

const PaymentPending: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  
  const paymentStatus = state?.paymentStatus;
  const message = state?.message || 'Tu pago está siendo procesado';

  useEffect(() => {
    // Si no hay datos de pago, redirigir al home
    if (!paymentStatus) {
      navigate('/');
    }
  }, [paymentStatus, navigate]);

  const handleContinue = () => {
    navigate('/');
  };

  const handleCheckStatus = () => {
    // Aquí podrías implementar una función para verificar el estado
    window.location.reload();
  };

  if (!paymentStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fff9ed_0%,#f8fafc_100%)] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-[2rem] border border-white/80 bg-white/95 p-8 text-center shadow-[0_28px_68px_-36px_rgba(180,83,9,0.28)]"
      >
        {/* ⏳ ICONO DE PENDIENTE */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="text-6xl mb-6"
        >
          ⏳
        </motion.div>

        {/* 📋 TÍTULO */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-2xl font-bold text-gray-800 mb-2"
        >
          Pago en revision
        </motion.h1>

        {/* 📝 MENSAJE */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-gray-600 mb-6"
        >
          {message}
        </motion.p>

        {/* 💰 DETALLES DEL PAGO */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-gray-50 rounded-lg p-4 mb-6"
        >
          <div className="text-sm text-gray-500 mb-2">Detalles del pago:</div>
          <div className="text-lg font-semibold text-gray-800">
            {paymentStatus.currency} {paymentStatus.amount.toFixed(2)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            ID: {paymentStatus.paymentId}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(paymentStatus.timestamp).toLocaleString()}
          </div>
        </motion.div>

        {/* 🎯 ESTADO DEL PAGO */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-yellow-100 border border-yellow-200 rounded-lg p-3 mb-6"
        >
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            <span className="text-yellow-800 font-medium">Estado: Pendiente</span>
          </div>
        </motion.div>

        {/* ℹ️ INFORMACIÓN ADICIONAL */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
        >
          <div className="text-sm text-blue-800">
            <strong>¿Qué significa esto?</strong>
            <br />
            Tu pago está siendo verificado. Esto puede tomar unos minutos dependiendo del método de pago utilizado.
          </div>
        </motion.div>

        {/* 🔄 BOTONES DE ACCIÓN */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="space-y-3"
        >
          <button
            onClick={handleCheckStatus}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Verificar estado
          </button>
          
          <button
            onClick={handleContinue}
            className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Volver al inicio
          </button>
        </motion.div>

        {/* 📧 NOTA */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="text-xs text-gray-500 mt-6"
        >
          Te notificaremos cuando el pago sea confirmado
        </motion.p>
      </motion.div>
    </div>
  );
};

export default PaymentPending;
